import React, { memo, useRef, useEffect, useState } from 'react';
import ItemsSwiper from '@wconcept/wconcept-ui-renewal/src/components/atoms/ItemsSwiper';
import SwiperNavigation from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperNavigation';
import SwiperPagination from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperPagination';
import Text from '@wconcept/wconcept-ui-renewal/src/components/atoms/Text';
import { Swiper as SwiperClass } from 'swiper/types';
import {
  getUniqueKey,
  classNameBind,
} from '@wconcept/wconcept-common/utils/commonUtils';

import * as S from './style';

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

interface IProps {
  colorType?: ColorType;
  title?: string;
  subTitle?: string;
  children?: React.JSX.Element | React.JSX.Element[];
  isDesktop?: boolean;
  onSlideChange?: (swiper: SwiperClass) => void;
}

const HighlightPickSection2 = ({
  colorType = 'white',
  title,
  subTitle,
  children,
  isDesktop = false,
  onSlideChange,
}: IProps) => {
  const isCustomColor = typeof colorType === 'object';
  const customColors = isCustomColor ? colorType : undefined;

  const cName = classNameBind([
    'highlight-pick',
    !isCustomColor ? `bg-type-${colorType}` : '',
  ]);

  const uniqueKey = `swiper-navi-${getUniqueKey()}`;
  const swiperRef = useRef<SwiperClass>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const childCnt = React.Children.count(children);

  const showControls = isDesktop && childCnt > 2;
  const paramPc: any = {
    slidesPerView: 2,
    slidesPerGroup: 2,
    spaceBetween: 32,
    loop: false,
    autoplay: false,
    pagination: {
      el: `.${uniqueKey}-page`,
      type: 'fraction',
      enabled: showControls,
    },
    navigation: {
      prevEl: `.${uniqueKey}-navi .swiper-button-prev`,
      nextEl: `.${uniqueKey}-navi .swiper-button-next`,
      enabled: showControls,
    },
    paginationEl: showControls ? (
      <SwiperPagination
        type="fraction02Medium"
        className={`${uniqueKey}-page`}
        bgType={colorType === 'black' ? 'black' : 'white'}
      />
    ) : null,
    navigationEl: showControls ? (
      <SwiperNavigation
        type="bottom01"
        size="medium"
        bgType={colorType === 'black' ? 'black' : 'white'}
        className={`${uniqueKey}-navi`}
      />
    ) : null,
  };

  // 모바일도 네이티브 스크롤(ItemsScrollBar) 대신 Swiper를 사용한다.
  // iOS는 스크롤러가 관성/스냅 애니메이션 중일 때 다시 터치하면 그 제스처를
  // 스크롤 스레드가 스크롤러에 귀속시켜버려서(하위 요소의 touch-action 무시),
  // 아이템 내부 이미지 Swiper가 pointercancel을 받고 이미지 대신 다음 아이템으로 넘어갔다.
  // Swiper는 transform 기반이라 네이티브 스크롤 가로채기가 발생하지 않는다.
  const paramMo: any = {
    className: 'highlight-pick-list',
    slidesPerView: 1,
    slidesPerGroup: 1,
    spaceBetween: 6,
    loop: false,
    autoplay: false,
  };

  useEffect(() => {
    if (swiperRef.current) {
      const $swiper = swiperRef.current;

      const handleTouchStart = () => {
        if ($swiper) {
          $swiper.wrapperEl.classList.add('on-touch');
        }
      };

      const handleTouchEnd = () => {
        setTimeout(() => {
          if ($swiper) {
            $swiper.wrapperEl.classList.remove('on-touch');
          }
        }, 200);
      };

      $swiper.on('sliderMove', handleTouchStart);
      $swiper.on('touchEnd', handleTouchEnd);

      return () => {
        $swiper.off('sliderMove', handleTouchStart);
        $swiper.off('touchEnd', handleTouchEnd);
      };
    }

    return undefined;
  }, []);

  useEffect(() => {
    const currentSection = sectionRef.current;
    if (!currentSection) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(currentSection);

    return () => {
      observer.unobserve(currentSection);
    };
  }, []);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isDesktop && isVisible) {
      let tick = 0;
      interval = setInterval(() => {
        const swiper = swiperRef.current;
        if (!swiper || !swiper.slides) return;

        const { activeIndex } = swiper;
        const activeSlide = swiper.slides[activeIndex];
        const nextSlide = swiper.slides[activeIndex + 1];

        const targetSlide = tick % 2 === 0 ? activeSlide : nextSlide;

        if (targetSlide) {
          const innerSwiperEl = targetSlide.querySelector(
            '.highlight-swiper .items-swiper-swiper'
          );
          if (innerSwiperEl && (innerSwiperEl as any).swiper) {
            const innerSwiper = (innerSwiperEl as any).swiper;
            innerSwiper.slideNext();
          }
        }
        // eslint-disable-next-line no-plusplus
        tick++;
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isDesktop, isVisible]);

  return (
    <S.HighlightPickSection2
      ref={sectionRef}
      className={cName}
      $bgColor={customColors?.bgColor}
      $textColor={customColors?.textColor}
    >
      {title && (
        <Text
          block
          className="content-title"
          weight={600}
          size={18}
          lineHeight="130%"
          color={customColors?.textColor}
        >
          {title}
        </Text>
      )}
      {subTitle && (
        <Text
          className="content-sub-title"
          size={13}
          lineHeight={15}
          color={customColors?.textColor ?? '#000'}
          block
        >
          {subTitle}
        </Text>
      )}
      {isDesktop ? (
        <ItemsSwiper
          {...paramPc}
          onSlideChange={onSlideChange}
          onSwiper={(swiper: SwiperClass) => {
            swiperRef.current = swiper;
          }}
        >
          {children}
        </ItemsSwiper>
      ) : (
        <ItemsSwiper {...paramMo}>{children}</ItemsSwiper>
      )}
    </S.HighlightPickSection2>
  );
};

export default memo(HighlightPickSection2);
